import React from "react";
import { cn } from "@/lib/utils";
import { motion } from "motion/react";

export const MacOSWallpaper = ({ children, className }: { children: React.ReactNode; className?: string }) => {
  return (
    <div className={cn("relative min-h-screen w-full bg-[#f0f0f5]", className)}>
      {/* Abstract Animated Gradient Mesh Background */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
         <motion.div 
           animate={{ 
             x: [0, 100, 0], 
             y: [0, -50, 0],
             scale: [1, 1.1, 1] 
           }}
           transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
           className="absolute top-[-20%] left-[-10%] w-[80%] h-[80%] rounded-full bg-[#ffdee2] mix-blend-multiply filter blur-[80px] opacity-60" 
         />
         <motion.div 
           animate={{ 
             x: [0, -80, 0], 
             y: [0, 60, 0],
             scale: [1, 1.2, 1]
           }}
           transition={{ duration: 25, repeat: Infinity, ease: "easeInOut", delay: 2 }}
           className="absolute top-[-10%] right-[-20%] w-[70%] h-[70%] rounded-full bg-[#d6eaff] mix-blend-multiply filter blur-[80px] opacity-60" 
         />
         <motion.div 
           animate={{ 
             x: [0, 60, 0], 
             y: [0, -40, 0],
             scale: [1, 0.9, 1]
           }}
           transition={{ duration: 22, repeat: Infinity, ease: "easeInOut", delay: 4 }}
           className="absolute bottom-[-20%] left-[20%] w-[70%] h-[80%] rounded-full bg-[#fff5d6] mix-blend-multiply filter blur-[80px] opacity-60" 
         />
         
         {/* Noise overlay for texture */}
         <div className="absolute inset-0 opacity-[0.04] bg-[url('https://grainy-gradients.vercel.app/noise.svg')]" />
      </div>
      
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};

export const GlassContainer = ({ 
  children, 
  className, 
  intensity = "medium",
  dark = false 
}: { 
  children: React.ReactNode; 
  className?: string;
  intensity?: "low" | "medium" | "high";
  dark?: boolean;
}) => {
  const backdrop = {
    low: dark 
      ? "backdrop-blur-md bg-black/30 border-white/10" 
      : "backdrop-blur-md bg-white/30 border-white/40",
    medium: dark
      ? "backdrop-blur-xl bg-black/40 border-white/10"
      : "backdrop-blur-xl bg-white/60 border-white/50",
    high: dark
      ? "backdrop-blur-2xl bg-black/60 border-white/10"
      : "backdrop-blur-2xl bg-white/80 border-white/60",
  };

  return (
    <div className={cn(
      "rounded-3xl border shadow-xl",
      backdrop[intensity],
      "shadow-[0_8px_32px_0_rgba(0,0,0,0.05)]",
      className
    )}>
      {children}
    </div>
  );
};

export const GlassCard = ({ children, className }: { children: React.ReactNode; className?: string }) => {
  return (
    <div className={cn(
      "rounded-2xl border border-white/40 bg-white/40 backdrop-blur-lg shadow-sm hover:bg-white/50 transition-all duration-300 hover:shadow-md",
      className
    )}>
      {children}
    </div>
  );
};
